/**
 * ConversationNetwork.tsx - VERSIÓN CORREGIDA
 * 
 * CAMBIOS PRINCIPALES:
 * 1. ✅ Loop de animación continuo funcionando
 * 2. ✅ Posición de cámara optimizada para visualización
 * 3. ✅ Sistema de nodos mejorado con posicionamiento correcto
 * 4. ✅ Herramientas de debug 3D integradas
 * 5. ✅ Controles de cámara mejorados
 * 6. ✅ Grid y iluminación optimizada
 * 
 * @author ANUBIS AI Team
 * @version 2.1.0 - Renderización Corregida
 */

// ==================== IMPORTS (SIN CAMBIOS) ====================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Row, Col, Button, Badge, Spinner, Alert, Dropdown, ButtonGroup, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faNetworkWired, 
    faList, 
    faTableCells, 
    faClock, 
    faRobot, 
    faUser, 
    faPause, 
    faPlay,
    faExpand,
    faCompress,
    faTimes,
    faFilter,
    faSync,
    faPhone,
    faEnvelope,
    faDollarSign,
    faEye,
    faUserTie,
    faBullseye,
    faHandshake,
    faFire,
    faComments,
    faHome,
    faDownload,
    faCube
} from '@fortawesome/free-solid-svg-icons';
import * as THREE from 'three';

// Importar el servicio de conversaciones del SellerBot
import { SellerConvService, type SellerMessage,type SellerConversation, type SendMessageRequest } from '../../service/sellerConvService';

// ==================== INTERFACES Y TIPOS (SIN CAMBIOS MAYORES) ====================

interface ConversationPoint3D {
    id: string;
    type: 'hub' | 'agent' | 'client';
    label: string;
    position: THREE.Vector3;
    color: string;
    size: number;
    metadata: {
        conversation?: any;
        agentData?: any;
        hubData?: any;
        isPaused?: boolean;
        isHumanControlled?: boolean;
    };
}

interface NetworkConnection {
    source: ConversationPoint3D;
    target: ConversationPoint3D;
    animated: boolean;
    strength: number;
}

interface ConversationNetworkData {
    points: ConversationPoint3D[];
    connections: NetworkConnection[];
    axes: {
        x: { label: string; range: [number, number] };
        y: { label: string; range: [number, number] };
        z: { label: string; range: [number, number] };
    };
}

interface ConversationData {
    id: string;
    contactName: string;
    contactPhone: string;
    contactEmail?: string;
    stage: string;
    value: number;
    probability: number;
    profileUsed: string;
    productOffered: string;
    lastMessage: string;
    lastMessageTime: string;
    messages: any[];
    isActive: boolean;
    whatsappChatId: string;
    source?: string;
}

interface AgentData {
    personality: string;
    activeConversations: number;
    totalValue: number;
    averageProbability: number;
}

interface HubData {
    totalConversations: number;
    totalValue: number;
    activeAgents: number;
}

interface ConversationNetworkProps {
    conversations: ConversationData[];
    loading: boolean;
    onRefresh: () => void;
    language: 'es' | 'en';
    userConfig?: {
        whatsAppServerPort: number;
        whatsAppApiPort: number;
        mlServerPort: number;
    };
    force3DMode?: boolean;
}

type ViewMode = '3d-network' | '2d-grid' | 'list' | 'timeline';

interface TooltipData {
    visible: boolean;
    x: number;
    y: number;
    point: ConversationPoint3D | null;
}

interface DebugInfo {
    fps: number;
    visibleNodes: number;
    totalNodes: number;
    cameraPosition: { x: number; y: number; z: number };
    renderTime: number;
}


// ==================== HOOK THREE.JS CORREGIDO ====================
/**
 * CAMBIO PRINCIPAL: Hook Three.js completamente reescrito para solucionar renderización
 * UBICACIÓN: Reemplazar todo el hook useConversationThree3D existente
 */
export const useConversationThree3D = ({
    enableControls = true,
    enableTooltips = true,
    backgroundColor = '#0a0a0a', // CAMBIO: Fondo más oscuro
    cameraPosition = { x: 80, y: 60, z: 80 }, // CAMBIO: Posición optimizada
    onPointClick,
    onPointHover,
    autoRotate = false,
    enableAnimation = true
}: {
    enableControls?: boolean;
    enableTooltips?: boolean;
    backgroundColor?: string;
    cameraPosition?: { x: number; y: number; z: number };
    onPointClick?: (point: ConversationPoint3D) => void;
    onPointHover?: (point: ConversationPoint3D | null) => void;
    autoRotate?: boolean;
    enableAnimation?: boolean;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<THREE.Scene>();
    const rendererRef = useRef<THREE.WebGLRenderer>();
    const cameraRef = useRef<THREE.PerspectiveCamera>();
    const animationFrameRef = useRef<number>();
    const nodesRef = useRef<Map<string, THREE.Mesh>>(new Map());
    const connectionsRef = useRef<THREE.Group>(new THREE.Group());
    const particlesRef = useRef<THREE.Group>(new THREE.Group());
    
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tooltip, setTooltip] = useState<TooltipData>({
        visible: false,
        x: 0,
        y: 0,
        point: null
    });
    const [debugInfo, setDebugInfo] = useState<DebugInfo>({
        fps: 0,
        visibleNodes: 0,
        totalNodes: 0,
        cameraPosition: { x: 0, y: 0, z: 0 },
        renderTime: 0
    });
    
    // NUEVO: Variables para control de cámara y debug
    const lastTime = useRef(0);
    const frameCount = useRef(0);
    const isAutoRotating = useRef(autoRotate);
    const mouseRef = useRef({ x: 0, y: 0 });
    const isDragging = useRef(false);
    const raycaster = useRef(new THREE.Raycaster(
        new THREE.Vector3(), // origin
        new THREE.Vector3(0, 0, -1), // direction
        0, // near
        Infinity // far
    ));
    const mouse = useRef(new THREE.Vector2());

    // CAMBIO MAYOR: Inicialización de escena completamente reescrita
    const initScene = useCallback(() => {
        if (!containerRef.current) {
            console.log('❌ Container no disponible');
            return;
        }

        try {
            console.log('🚀 Inicializando escena Three.js mejorada...');

            // Limpiar container
            containerRef.current.innerHTML = '';

            // === ESCENA ===
            const scene = new THREE.Scene();
            scene.background = new THREE.Color(backgroundColor);
            scene.fog = new THREE.Fog(new THREE.Color(backgroundColor), 100, 400); // CAMBIO: Fog más lejano

            // === CÁMARA OPTIMIZADA ===
            const camera = new THREE.PerspectiveCamera(
                60, // CAMBIO: FOV reducido para mejor vista
                containerRef.current.clientWidth / containerRef.current.clientHeight,
                0.1,
                1000
            );
            (camera as THREE.PerspectiveCamera).position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
            camera.lookAt(0, 0, 0);

            // === RENDERER OPTIMIZADO ===
            const renderer = new THREE.WebGLRenderer({ 
                antialias: true, 
                alpha: false, // CAMBIO: Sin transparencia para mejor rendimiento
                powerPreference: "high-performance",
                precision: "mediump" // CAMBIO: Precisión media para mejor rendimiento
            });
            renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            renderer.shadowMap.enabled = true;
            renderer.shadowMap.type = THREE.PCFSoftShadowMap;
            renderer.outputColorSpace = THREE.SRGBColorSpace; // CAMBIO: Color space moderno

            // === ILUMINACIÓN MEJORADA ===
            // Luz ambiental más intensa
            const ambientLight = new THREE.AmbientLight(0x404040, 0.6); // CAMBIO: Más intensa
            scene.add(ambientLight);

            // Luz direccional principal
            const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2); // CAMBIO: Más intensa
            directionalLight.position.set(100, 100, 50);
            directionalLight.castShadow = true;
            directionalLight.shadow.mapSize.width = 1024;
            directionalLight.shadow.mapSize.height = 1024;
            directionalLight.shadow.camera.near = 0.5;
            directionalLight.shadow.camera.far = 500;
            scene.add(directionalLight);

            // NUEVO: Luz del hub ANUBIS más prominente
            const hubLight = new THREE.PointLight(0x8e44ad, 2, 200);
            hubLight.position.set(0, 0, 0);
            scene.add(hubLight);

            // NUEVO: Luces adicionales para agentes
            const agentLight1 = new THREE.PointLight(0x3498db, 0.8, 100);
            agentLight1.position.set(40, 20, 0);
            scene.add(agentLight1);

            const agentLight2 = new THREE.PointLight(0x3498db, 0.8, 100);
            agentLight2.position.set(-40, 20, 0);
            scene.add(agentLight2);

            // === GRID MEJORADO ===
            const gridHelper = new THREE.GridHelper(200, 40, 0x666666, 0x333333); // CAMBIO: Más líneas y más visible
            gridHelper.position.y = -30; // CAMBIO: Más cerca
            scene.add(gridHelper);

            // NUEVO: Ejes de coordenadas para debug
            const axesHelper = new THREE.AxesHelper(50);
            scene.add(axesHelper);

            // === GRUPOS ===
            scene.add(connectionsRef.current);
            scene.add(particlesRef.current);

            // === CONTROLES MEJORADOS ===
            if (enableControls) {
                setupControls(renderer, camera);
            }

            // Asignar referencias
            sceneRef.current = scene;
            cameraRef.current = camera;
            rendererRef.current = renderer;

            // Agregar canvas al DOM
            containerRef.current.appendChild(renderer.domElement);

            console.log('✅ Escena Three.js inicializada exitosamente');
            setIsLoading(false);

            console.log('🎬 Ejecutando render inicial forzado...');
renderer.render(scene, camera);
console.log('✅ Render inicial ejecutado');


        } catch (err) {
            console.error('❌ Error inicializando Three.js:', err);
            setError('Error inicializando visualización 3D');
            setIsLoading(false);
        }
    }, [backgroundColor, cameraPosition, enableControls]);

    // NUEVA FUNCIÓN: Setup de controles mejorados
const setupControls = useCallback((renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera) => {
    console.log('🎮 Configurando controles simplificados...');
    
    let isDragging = false;
    let previousMousePosition = { x: 0, y: 0 };

    // Mouse down - SIMPLIFICADO
    const onMouseDown = (e: MouseEvent) => {
        isDragging = true;
        previousMousePosition = { x: e.clientX, y: e.clientY };
        renderer.domElement.style.cursor = 'grabbing';
        console.log('🖱️ Mouse down - iniciando drag');
    };

    // Mouse move - SIMPLIFICADO
    const onMouseMove = (e: MouseEvent) => {
        // Actualizar mouse para raycasting
        const rect = renderer.domElement.getBoundingClientRect();
        mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        if (!isDragging) return;

        const deltaMove = {
            x: e.clientX - previousMousePosition.x,
            y: e.clientY - previousMousePosition.y
        };

        // Rotación orbital SIMPLIFICADA
        const spherical = new THREE.Spherical();
        spherical.setFromVector3(camera.position);
        
        spherical.theta -= deltaMove.x * 0.01;
        spherical.phi += deltaMove.y * 0.01;
        
        // Limitar phi
        spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
        
        camera.position.setFromSpherical(spherical);
        camera.lookAt(0, 0, 0);

        previousMousePosition = { x: e.clientX, y: e.clientY };
        
        console.log('🔄 Cámara rotada:', {
            x: Math.round(camera.position.x),
            y: Math.round(camera.position.y), 
            z: Math.round(camera.position.z)
        });
    };

    // Mouse up - SIMPLIFICADO
    const onMouseUp = () => {
        isDragging = false;
        renderer.domElement.style.cursor = 'grab';
        console.log('🖱️ Mouse up - fin drag');
    };

    // Wheel zoom - SIMPLIFICADO
    const onWheel = (e: WheelEvent) => {
        const zoomSpeed = 0.1;
        const direction = e.deltaY > 0 ? 1 : -1;
        const distance = camera.position.distanceTo(new THREE.Vector3(0, 0, 0));
        
        // Limitar zoom
        if ((distance > 20 && direction > 0) || (distance < 200 && direction < 0)) {
            camera.position.multiplyScalar(1 + direction * zoomSpeed);
            camera.lookAt(0, 0, 0);
            console.log('🔍 Zoom:', direction > 0 ? 'out' : 'in', 'Distancia:', Math.round(distance));
        }
        
        e.preventDefault();
    };

    // Click detection - SIMPLIFICADO
    const onClick = (e: MouseEvent) => {
        if (!sceneRef.current || !cameraRef.current) return;

        raycaster.current.setFromCamera(mouse.current, camera);
        const intersects = raycaster.current.intersectObjects(
            Array.from(nodesRef.current.values())
        );

        if (intersects.length > 0) {
            const clickedObject = intersects[0].object as THREE.Mesh;
            const pointData = (clickedObject as any).pointData as ConversationPoint3D | undefined;
            
            if (pointData && onPointClick) {
                console.log('🎯 Click en nodo:', pointData.label);
                onPointClick(pointData);
            }
        }
    };

    // Agregar event listeners
    renderer.domElement.addEventListener('mousedown', onMouseDown);
    renderer.domElement.addEventListener('mousemove', onMouseMove);
    renderer.domElement.addEventListener('mouseup', onMouseUp);
    renderer.domElement.addEventListener('wheel', onWheel);
    renderer.domElement.addEventListener('click', onClick);
    
    renderer.domElement.style.cursor = 'grab';
    
    console.log('✅ Controles configurados');
}, [onPointClick]);

    // CAMBIO MAYOR: Función de creación de nodos mejorada
    const createNode = useCallback((point: ConversationPoint3D): THREE.Mesh => {
    let geometry: THREE.BufferGeometry;
    let material: THREE.Material;

    console.log(`🔨 Creando nodo: ${point.label} (${point.type}) en:`, point.position);

    switch (point.type) {
        case 'hub':
            geometry = new THREE.IcosahedronGeometry(point.size * 1.5, 2);
            material = new THREE.MeshPhongMaterial({
                color: point.color,
                emissive: point.color,
                emissiveIntensity: 0.4,
                shininess: 100,
                transparent: true,
                opacity: 0.95
            });
            break;
            
        case 'agent':
            geometry = new THREE.SphereGeometry(point.size * 1.2, 20, 16);
            material = new THREE.MeshPhongMaterial({
                color: point.color,
                emissive: point.color,
                emissiveIntensity: 0.3,
                shininess: 80,
                transparent: true,
                opacity: 0.9
            });
            break;
            
        case 'client':
            geometry = new THREE.BoxGeometry(point.size, point.size, point.size);
            material = new THREE.MeshLambertMaterial({
                color: point.color,
                transparent: true,
                opacity: 0.8
            });
            break;
            
        default:
            geometry = new THREE.SphereGeometry(8, 12, 8);
            material = new THREE.MeshBasicMaterial({ color: 0x888888 });
    }

    const mesh = new THREE.Mesh(geometry, material);
    (mesh as any).pointData = point;
    mesh.position.copy(point.position);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    // CORRECCIÓN: Asignar datos correctamente tipados
    //mesh.pointData = point;

    // Animación de entrada
    mesh.scale.set(0.1, 0.1, 0.1);
    const targetScale = new THREE.Vector3(1, 1, 1);
    
    const animateScale = () => {
        mesh.scale.lerp(targetScale, 0.1);
        if (mesh.scale.x < 0.99) {
            requestAnimationFrame(animateScale);
        } else {
            mesh.scale.copy(targetScale);
        }
    };
    animateScale();

    return mesh;
}, []);

    // CAMBIO MAYOR: Función updateData mejorada
    const updateData = useCallback((data: ConversationNetworkData) => {
        console.log('🔧 Actualizando datos del network 3D');
        console.log('📊 Puntos a renderizar:', data.points.length);
        console.log('🔗 Conexiones a crear:', data.connections.length);

        if (!sceneRef.current || !rendererRef.current || !cameraRef.current) {
            console.log('⏳ Escena no lista, reintentando...');
            setTimeout(() => updateData(data), 100);
            return;
        }

        try {
            // Limpiar nodos anteriores
            nodesRef.current.forEach(node => {
                sceneRef.current?.remove(node);
                node.geometry.dispose();
                if (Array.isArray(node.material)) {
                    node.material.forEach(mat => mat.dispose());
                } else {
                    node.material.dispose();
                }
            });
            nodesRef.current.clear();

            // Limpiar conexiones anteriores
            connectionsRef.current.clear();
            particlesRef.current.clear();

            // Crear nuevos nodos
            data.points.forEach((point, index) => {
                console.log(`🎯 Creando nodo ${index + 1}/${data.points.length}: ${point.label}`);
                const node = createNode(point);
                sceneRef.current?.add(node);
                nodesRef.current.set(point.id, node);
            });

            // Crear conexiones
            data.connections.forEach((connection, index) => {
                console.log(`🔗 Creando conexión ${index + 1}/${data.connections.length}`);
                const line = createConnection(connection);
                connectionsRef.current.add(line);

                // Partículas de flujo para conexiones animadas
                if (connection.animated && enableAnimation) {
                    const particles = createFlowParticles(connection);
                    particlesRef.current.add(particles);
                }
            });

            console.log('✅ Network 3D actualizado exitosamente');
            console.log(`📊 Total nodos en escena: ${nodesRef.current.size}`);
            
        } catch (err) {
            console.error('❌ Error actualizando network 3D:', err);
            setError('Error actualizando visualización 3D');
        }
    }, [createNode, enableAnimation]);

    // NUEVA FUNCIÓN: Crear conexiones mejoradas
    const createConnection = useCallback((connection: NetworkConnection): THREE.Line => {
        const points = [
            connection.source.position,
            connection.target.position
        ];
        
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        
        // Material mejorado con gradiente
        const opacity = connection.animated ? 0.9 : 0.4;
        const color = connection.animated ? 0x00ff88 : 0x888888; // CAMBIO: Verde más visible
        
        const material = new THREE.LineBasicMaterial({
            color: color,
            transparent: true,
            opacity: opacity,
            linewidth: Math.max(1, connection.strength * 3) // CAMBIO: Líneas más gruesas
        });

        const line = new THREE.Line(geometry, material);
        (line as any).connectionData = connection;
        
        return line;
    }, []);

    // NUEVA FUNCIÓN: Crear partículas de flujo mejoradas
    const createFlowParticles = useCallback((connection: NetworkConnection): THREE.Points => {
        const particleCount = 15; // CAMBIO: Más partículas
        const positions = new Float32Array(particleCount * 3);
        
        for (let i = 0; i < particleCount; i++) {
            const t = i / (particleCount - 1);
            const pos = new THREE.Vector3().lerpVectors(
                connection.source.position,
                connection.target.position,
                t
            );
            positions[i * 3] = pos.x;
            positions[i * 3 + 1] = pos.y;
            positions[i * 3 + 2] = pos.z;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        const material = new THREE.PointsMaterial({
            color: 0x00ffff,
            size: 3, // CAMBIO: Partículas más grandes
            transparent: true,
            opacity: 0.9,
            blending: THREE.AdditiveBlending
        });

        const particles = new THREE.Points(geometry, material);
        (particles as any).connectionData = connection;
        (particles as any).isFlowParticles = true;
        
        return particles;
    }, []);

    // CAMBIO MAYOR: Loop de animación completamente reescrito
const animate = useCallback(() => {
    // NUEVO: Logging simplificado solo cada 60 frames para no spam
    if (frameCount.current % 60 === 0) {
        console.log('🎬 animate() corriendo - Frame:', frameCount.current);
    }
    
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) {
        // CRÍTICO: Asegurar que el loop continúe SIEMPRE
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
    }

    const currentTime = performance.now();
    
    // Calcular FPS
    if (currentTime - lastTime.current >= 1000) {
        const fps = Math.round((frameCount.current * 1000) / (currentTime - lastTime.current));
        setDebugInfo(prev => ({
            ...prev,
            fps,
            visibleNodes: nodesRef.current.size,
            totalNodes: nodesRef.current.size,
            cameraPosition: {
                x: Math.round(cameraRef.current?.position.x || 0),
                y: Math.round(cameraRef.current?.position.y || 0),
                z: Math.round(cameraRef.current?.position.z || 0)
            },
            renderTime: performance.now() - currentTime
        }));
        
        frameCount.current = 0;
        lastTime.current = currentTime;
    }
    frameCount.current++;

    // Auto-rotación
    if (isAutoRotating.current && cameraRef.current) {
        const radius = cameraRef.current.position.distanceTo(new THREE.Vector3(0, 0, 0));
        const theta = currentTime * 0.0005;
        
        cameraRef.current.position.x = Math.cos(theta) * radius;
        cameraRef.current.position.z = Math.sin(theta) * radius;
        cameraRef.current.lookAt(0, 0, 0);
    }

    // Animar nodos
    if (enableAnimation) {
        nodesRef.current.forEach((node, id) => {
            const pointData = (node as any).pointData;
            if (pointData?.type === 'hub') {
                const time = currentTime * 0.001;
                const scale = 1 + Math.sin(time * 2) * 0.1;
                node.scale.set(scale, scale, scale);
                node.rotation.y = time * 0.5;
            } else if (pointData?.type === 'agent') {
                node.rotation.y += 0.01;
            }
        });

        // Animar partículas de flujo
        particlesRef.current.children.forEach(particles => {
            if ((particles as any).isFlowParticles) {
                const time = currentTime * 0.003;
                particles.rotation.z = time;
                
                const scale = 1 + Math.sin(time * 5) * 0.2;
                particles.scale.set(scale, scale, scale);
            }
        });
    }

    // CRÍTICO: Render la escena
    rendererRef.current.render(sceneRef.current, cameraRef.current);
    
    // CRÍTICO: Continuar el loop SIEMPRE
    animationFrameRef.current = requestAnimationFrame(animate);
}, [enableAnimation]);

    // NUEVAS FUNCIONES: Funciones de control
    const resetCamera = useCallback(() => {
        if (cameraRef.current) {
            console.log('🏠 Reseteando cámara');
            cameraRef.current.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
            cameraRef.current.lookAt(0, 0, 0);
        }
    }, [cameraPosition]);

    const toggleAutoRotate = useCallback(() => {
        isAutoRotating.current = !isAutoRotating.current;
        console.log('🔄 Auto-rotación:', isAutoRotating.current ? 'activada' : 'desactivada');
    }, []);

    const exportImage = useCallback(async (): Promise<string> => {
        if (!rendererRef.current) {
            throw new Error('Renderer no disponible');
        }
        
        console.log('📸 Exportando imagen...');
        if (sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
        
        return rendererRef.current.domElement.toDataURL('image/png');
    }, []);

    const clearScene = useCallback(() => {
        console.log('🧹 Limpiando escena 3D');
        nodesRef.current.forEach(node => {
            sceneRef.current?.remove(node);
            node.geometry.dispose();
            if (Array.isArray(node.material)) {
                node.material.forEach(mat => mat.dispose());
            } else {
                node.material.dispose();
            }
        });
        nodesRef.current.clear();
        
        connectionsRef.current.clear();
        particlesRef.current.clear();
    }, []);

    // CAMBIO: Manejo de resize mejorado
    const handleResize = useCallback(() => {
        if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;

        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;

        console.log(`📐 Resize: ${width}x${height}`);

        cameraRef.current.aspect = width / height;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(width, height);
    }, []);

    // ==================== EFFECTS ====================
    
    // Inicialización
    useEffect(() => {
        console.log('🚀 Iniciando hook Three.js');
        initScene();
        
        const resizeObserver = new ResizeObserver(handleResize);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            console.log('🧹 Limpiando hook Three.js');
            resizeObserver.disconnect();
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            //clearScene();
            if (rendererRef.current) {
                rendererRef.current.dispose();
            }
        };
    }, [initScene, handleResize, clearScene]);

    // Loop de animación
useEffect(() => {
    console.log('🔄 Animation effect triggered:', { isLoading, error: !!error });
    
    // NUEVO: Iniciar SIEMPRE el loop, independientemente del estado
    console.log('🚀 INICIANDO loop de animación - FORZADO MÚLTIPLE');
    
    // Múltiples intentos para asegurar inicio
    const startLoop = () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        animate();
    };
    
    // Intentos múltiples con diferentes delays
    setTimeout(startLoop, 50);
    setTimeout(startLoop, 200);
    setTimeout(startLoop, 500);
    
    return () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
    };
}, [animate]); 

    // Auto-rotate control
    useEffect(() => {
        isAutoRotating.current = autoRotate;
    }, [autoRotate]);

    return {
        containerRef,
        isLoading,
        error,
        tooltip, // Simplificado por ahora
        updateData,
        resetCamera,
        toggleAutoRotate,
        exportImage,
        clearScene,
        debugInfo // NUEVO: Información de debug
    };
};

// ==================== HOOK PARA DATOS DEL NETWORK (CONTINUACIÓN) ====================

/**
 * CAMBIO MENOR: Hook para datos del network con mejor logging
 * UBICACIÓN: Reemplazar useConversationNetworkData existente
 */
const useConversationNetworkData = (
    conversations: ConversationData[],
    filters: {
        stageFilter: string;
        agentFilter: string;
    }
) => {
    const [data, setData] = useState<ConversationNetworkData | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const processConversationData = useCallback(() => {
        console.log('🔄 Procesando datos de conversación...');
        console.log('📊 Input conversations:', conversations.length);
        console.log('🔍 Filtros aplicados:', filters);
        
        setIsProcessing(true);
        setError(null);

        try {
            // Filtrar conversaciones
            const filteredConversations = conversations.filter(conv => {
                if (filters.stageFilter !== 'all' && conv.stage !== filters.stageFilter) return false;
                if (filters.agentFilter !== 'all' && conv.profileUsed !== filters.agentFilter) return false;
                return true;
            });

            console.log('✅ Conversaciones filtradas:', filteredConversations.length);

            const points: ConversationPoint3D[] = [];
            const connections: NetworkConnection[] = [];

            // === NODO CENTRAL ANUBIS ===
            const hubData: HubData = {
                totalConversations: filteredConversations.length,
                totalValue: filteredConversations.reduce((sum, c) => sum + c.value, 0),
                activeAgents: [...new Set(filteredConversations.map(c => c.profileUsed))].length
            };

            const hubPoint: ConversationPoint3D = {
                id: 'anubis-hub',
                type: 'hub',
                label: 'ANUBIS AI',
                position: new THREE.Vector3(0, 0, 0), // Centro absoluto
                color: '#8e44ad',
                size: 15, // CAMBIO: Tamaño base más grande
                metadata: { hubData }
            };
            points.push(hubPoint);
            console.log('🏢 Hub ANUBIS creado:', hubPoint);

            // === NODOS DE AGENTES ===
            // CAMBIO: Lista fija de agentes para asegurar consistencia
            const allAgents = ['aggressive', 'consultive', 'educational', 'friendly'];
            const conversationAgents = [...new Set(filteredConversations.map(c => c.profileUsed))];
            const agents = [...new Set([...allAgents, ...conversationAgents])];

            const agentPositions: { [key: string]: THREE.Vector3 } = {};
            const agentRadius = 35; // CAMBIO: Radio más grande

            agents.forEach((agent, index) => {
                const agentConversations = filteredConversations.filter(c => c.profileUsed === agent);
                const angle = (index / agents.length) * Math.PI * 2;
                
                const agentData: AgentData = {
                    personality: agent,
                    activeConversations: agentConversations.length,
                    totalValue: agentConversations.reduce((sum, c) => sum + c.value, 0),
                    averageProbability: agentConversations.length > 0 ? 
                        agentConversations.reduce((sum, c) => sum + c.probability, 0) / agentConversations.length : 0
                };

                // CAMBIO: Posicionamiento mejorado con variación en Y
                const position = new THREE.Vector3(
                    Math.cos(angle) * agentRadius,
                    Math.sin(index * 1.5) * 8, // Variación vertical
                    Math.sin(angle) * agentRadius
                );

                agentPositions[agent] = position;

                // CAMBIO: Colores específicos por agente
                const agentColors = {
                    'aggressive': '#e74c3c',
                    'consultive': '#3498db',
                    'educational': '#f39c12',
                    'friendly': '#27ae60'
                };

                const agentPoint: ConversationPoint3D = {
                    id: `agent-${agent}`,
                    type: 'agent',
                    label: agent.charAt(0).toUpperCase() + agent.slice(1),
                    position,
                    color: agentColors[agent as keyof typeof agentColors] || '#3498db',
                    size: 8 + (agentData.activeConversations * 1.5), // CAMBIO: Tamaño dinámico
                    metadata: { agentData }
                };
                points.push(agentPoint);

                console.log(`🤖 Agente ${agent} creado:`, {
                    conversations: agentData.activeConversations,
                    position: position,
                    size: agentPoint.size
                });

                // Conexión hub -> agente
                connections.push({
                    source: hubPoint,
                    target: agentPoint,
                    animated: agentData.activeConversations > 0,
                    strength: Math.min(1, agentData.activeConversations / Math.max(1, filteredConversations.length))
                });
            });

            // === NODOS DE CLIENTES ===
            filteredConversations.forEach((conversation, index) => {
                const agentPosition = agentPositions[conversation.profileUsed];
                if (!agentPosition) {
                    console.warn(`⚠️ Posición de agente no encontrada para: ${conversation.profileUsed}`);
                    return;
                }

                // CAMBIO: Distribución mejorada alrededor del agente
                const clientAngle = (index / filteredConversations.length) * Math.PI * 2;
                const clientRadius = 12 + Math.random() * 8; // Radio variable
                const heightVariation = (Math.random() - 0.5) * 12;

                const position = new THREE.Vector3(
                    agentPosition.x + Math.cos(clientAngle) * clientRadius,
                    agentPosition.y + heightVariation,
                    agentPosition.z + Math.sin(clientAngle) * clientRadius
                );

                // CAMBIO: Colores mejorados por etapa
                const stageColors = {
                    'prospecting': '#74b9ff',
                    'interest': '#fdcb6e',
                    'negotiation': '#fd79a8',
                    'closing': '#00b894',
                    'closed': '#00cec9',
                    'lost': '#636e72'
                };

                const clientPoint: ConversationPoint3D = {
                    id: `client-${conversation.id}`,
                    type: 'client',
                    label: conversation.contactName || conversation.contactPhone,
                    position,
                    color: stageColors[conversation.stage as keyof typeof stageColors] || '#bdc3c7',
                    size: 4 + (conversation.probability / 25), // CAMBIO: Tamaño basado en probabilidad
                    metadata: {
                        conversation,
                        isPaused: false,
                        isHumanControlled: false
                    }
                };
                points.push(clientPoint);

                console.log(`👤 Cliente ${conversation.contactName} creado:`, {
                    stage: conversation.stage,
                    probability: conversation.probability,
                    agent: conversation.profileUsed,
                    position: position
                });

                // Conexión agente -> cliente
                const agentPoint = points.find(p => p.id === `agent-${conversation.profileUsed}`);
                if (agentPoint) {
                    connections.push({
                        source: agentPoint,
                        target: clientPoint,
                        animated: conversation.isActive,
                        strength: conversation.probability / 100
                    });
                }
            });

            const networkData: ConversationNetworkData = {
                points,
                connections,
                axes: {
                    x: { label: 'Distribución de Agentes', range: [-50, 50] },
                    y: { label: 'Nivel de Actividad', range: [-20, 20] },
                    z: { label: 'Dispersión de Clientes', range: [-50, 50] }
                }
            };

            console.log('✅ Red de datos procesada:', {
                totalPoints: networkData.points.length,
                totalConnections: networkData.connections.length,
                hubPoints: networkData.points.filter(p => p.type === 'hub').length,
                agentPoints: networkData.points.filter(p => p.type === 'agent').length,
                clientPoints: networkData.points.filter(p => p.type === 'client').length
            });

            setData(networkData);

        } catch (err) {
            console.error('❌ Error procesando datos de conversación:', err);
            setError('Error al procesar datos de conversaciones');
        } finally {
            setIsProcessing(false);
        }
    }, [conversations, filters.stageFilter, filters.agentFilter]);

    useEffect(() => {
        console.log('🔄 Hook de datos ejecutado - Trigger:', {
            conversationsLength: conversations.length,
            stageFilter: filters.stageFilter,
            agentFilter: filters.agentFilter
        });
        processConversationData();
    }, [conversations.length, filters.stageFilter, filters.agentFilter]);

    return {
        data,
        isProcessing,
        error,
        refreshData: processConversationData
    };
};

// ==================== HOOK PARA DATOS REALES DEL SELLERBOT ====================

/**
 * CAMBIO MENOR: Hook para obtener conversaciones reales con mejor logging
 * UBICACIÓN: Reemplazar useSellerBotConversations existente
 */
const useSellerBotConversations = (
    autoRefresh: boolean = true,
    userConfig?: {
        whatsAppServerPort: number;
        whatsAppApiPort: number;
        mlServerPort: number;
    }
): {
    conversations: SellerConversation[];
    loading: boolean;
    error: string | null;
    lastFetch: Date;
    refetch: () => Promise<void>;
    sendMessage: (conversationId: string, message: string) => Promise<any>;
} => {
    const [conversations, setConversations] = useState<SellerConversation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastFetch, setLastFetch] = useState<Date>(new Date());
    const [isFetching, setIsFetching] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const defaultConfig = {
        whatsAppServerPort: 3001,
        whatsAppApiPort: 3002,
        mlServerPort: 8002
    };

    const config = userConfig || defaultConfig;

    const fetchConversations = useCallback(async () => {
        if (isFetching) {
            console.log('⏳ Ya hay una consulta en progreso, saltando...');
            return;
        }
        setIsFetching(true);
        try {
            setError(null);
            console.log('🔄 Obteniendo conversaciones del SellerBot...');
            
            const sellerConversations = await SellerConvService.getSellerBotConversations(config);
            
            console.log(`✅ Obtenidas ${sellerConversations.length} conversaciones del SellerBot`);
            setConversations(sellerConversations);
            setLastFetch(new Date());
            
        } catch (err: any) {
            console.error('❌ Error obteniendo conversaciones del SellerBot:', err);
            setError(err.message || 'Error al obtener conversaciones del SellerBot');
        } finally {
            setLoading(false);
            setIsFetching(false);
        }
    }, [config]);

    // Carga inicial
    useEffect(() => {
        setLoading(true);
        fetchConversations();
    }, []);

    // Auto-refresh
    useEffect(() => {
        if (!autoRefresh) return;

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            console.log('🧹 Limpiando intervalo anterior');
        }

        intervalRef.current = setInterval(() => {
            console.log('🔄 Auto-refresh de conversaciones del SellerBot');
            fetchConversations();
        }, 5 * 60 * 1000); // Cada 5 minutos
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                console.log('🧹 Intervalo de auto-refresh limpiado');
            }
        };
    }, [autoRefresh]);

    // Función para enviar mensaje
    const sendMessage = useCallback(async (conversationId: string, message: string) => {
        try {
            const sellerConv = conversations.find(c => c.id === conversationId);
            if (!sellerConv) {
                throw new Error('Conversación no encontrada');
            }

            const phoneNumber = sellerConv.contactPhone.replace(/\D/g, '');
            const formattedMessage = `🤖 ${message}`;

            const result = await SellerConvService.sendMessage({
                to: phoneNumber,
                message: formattedMessage,
                conversationId: conversationId,
                mlStuff: config
            });

            if (result.success) {
                setTimeout(() => fetchConversations(), 2000);
                return result;
            } else {
                throw new Error(result.error || 'Error enviando mensaje');
            }
        } catch (error: any) {
            console.error('❌ Error enviando mensaje:', error);
            throw error;
        }
    }, [conversations, config]);

    return {
        conversations,
        loading,
        error,
        lastFetch,
        refetch: () => fetchConversations(),
        sendMessage
    };
};

// ==================== COMPONENTE PRINCIPAL ====================

/**
 * CAMBIO MENOR: Componente principal con mejor debug y información visual
 * UBICACIÓN: Reemplazar el componente ConversationNetwork existente
 */
const ConversationNetwork: React.FC<ConversationNetworkProps> = ({
    conversations,
    loading,
    onRefresh,
    language,
    userConfig
}) => {
    // ==================== ESTADOS PRINCIPALES ====================
    const [viewMode, setViewMode] = useState<ViewMode>('2d-grid'); //3d-network
    const [selectedConversation, setSelectedConversation] = useState<ConversationData | null>(null);
    const [showConversationModal, setShowConversationModal] = useState(false);
    const [stageFilter, setStageFilter] = useState('all');
    const [agentFilter, setAgentFilter] = useState('all');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [autoRotateEnabled, setAutoRotateEnabled] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    
    // Estados del modal drag&drop (sin cambios)
    const [modalPosition, setModalPosition] = useState({ x: 100, y: 100 });
    const [modalSize, setModalSize] = useState({ width: 800, height: 600 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    
    // Estados de control
    const [pausedConversations, setPausedConversations] = useState<Set<string>>(new Set());
    const [humanControlled, setHumanControlled] = useState<Set<string>>(new Set());

    // NUEVO: Estado para mostrar debug
    const [showDebugInfo, setShowDebugInfo] = useState(false);

    // ==================== HOOKS PERSONALIZADOS ====================
    
    // Hook para datos reales del SellerBot
    const {
        conversations: sellerBotConversations,
        loading: sellerBotLoading,
        error: sellerBotError,
        lastFetch,
        refetch: refetchSellerBot,
        sendMessage: sendSellerBotMessage
    } = useSellerBotConversations(true, userConfig);

    // Determinar qué conversaciones usar
    const activeConversations: ConversationData[] = sellerBotConversations.length > 0 
        ? sellerBotConversations.map((sellerConv: SellerConversation): ConversationData => ({
            id: sellerConv.id,
            contactName: sellerConv.contactName,
            contactPhone: sellerConv.contactPhone,
            contactEmail: sellerConv.contactEmail,
            stage: sellerConv.stage,
            value: sellerConv.value,
            probability: sellerConv.probability,
            profileUsed: sellerConv.profileUsed,
            productOffered: sellerConv.productOffered,
            lastMessage: sellerConv.preview,
            lastMessageTime: sellerConv.lastMessageTime,
            messages: sellerConv.messages.map((msg: SellerMessage) => ({
                id: msg.id,
                timestamp: msg.timestamp,
                sender: msg.sender === 'bot' ? 'bot' : 'client',
                text: msg.text,
                type: msg.type,
                status: msg.status
            })),
            isActive: sellerConv.isActive,
            whatsappChatId: sellerConv.whatsappChatId,
            source: sellerConv.source
        }))
        : conversations; 
    
    const isLoadingData = sellerBotLoading || loading;
    const dataError = sellerBotError;

    // Hook para datos del network
    const networkData = useConversationNetworkData(activeConversations, {
        stageFilter,
        agentFilter
    });

    // Hook para Three.js con debug
    const {
        containerRef,
        isLoading: threeLoading,
        error: threeError,
        tooltip,
        updateData,
        resetCamera,
        toggleAutoRotate,
        exportImage,
        clearScene,
        debugInfo // NUEVO: Información de debug
    } = useConversationThree3D({
        enableControls: true,
        enableTooltips: true,
        autoRotate: autoRotateEnabled,
        onPointClick: (point) => {
            if (point.metadata.conversation) {
                setSelectedConversation(point.metadata.conversation);
                setShowConversationModal(true);
            }
        },
        onPointHover: (point) => {
            // Manejar hover del tooltip
        }
    });

    // ==================== FUNCIONES DE CONTROL ====================
    
    const handleRefresh = useCallback(() => {
        console.log('🔄 Refrescando datos de conversaciones...');
        refetchSellerBot();
        if (onRefresh) {
            onRefresh();
        }
    }, [refetchSellerBot, onRefresh]);

    const handleSendMessage = useCallback(async (conversationId: string, message: string) => {
        try {
            if (sellerBotConversations.length > 0) {
                await sendSellerBotMessage(conversationId, message);
                console.log(`✅ Mensaje enviado a ${conversationId}`);
            } else {
                console.log(`📧 Mock: Enviando mensaje a ${conversationId}: ${message}`);
            }
            setShowConversationModal(false);
        } catch (error: any) {
            console.error('❌ Error enviando mensaje:', error);
            alert(`Error enviando mensaje: ${error.message}`);
        }
    }, [sellerBotConversations, sendSellerBotMessage]);

    // Funciones de control para pausar/reanudar conversaciones
    const toggleConversationPause = async (conversationId: string) => {
        try {
            const isPaused = pausedConversations.has(conversationId);
            
            if (sellerBotConversations.length > 0) {
                console.log(`🤖 ${isPaused ? 'Reanudando' : 'Pausando'} conversación del SellerBot: ${conversationId}`);
            } else {
                console.log(`POST /api/sales-bot/conversations/${conversationId}/toggle-pause`, {
                    action: isPaused ? 'resume' : 'pause',
                    user_controlled: false
                });
            }

            const newPaused = new Set(pausedConversations);
            if (isPaused) {
                newPaused.delete(conversationId);
            } else {
                newPaused.add(conversationId);
            }
            setPausedConversations(newPaused);

        } catch (error) {
            console.error('Error toggling conversation pause:', error);
        }
    };

    const toggleHumanControl = async (conversationId: string) => {
        try {
            const isHumanControlled = humanControlled.has(conversationId);
            
            console.log(`POST /api/sales-bot/conversations/${conversationId}/toggle-human-control`, {
                action: 'human_control',
                user_controlled: !isHumanControlled
            });

            const newHumanControlled = new Set(humanControlled);
            if (isHumanControlled) {
                newHumanControlled.delete(conversationId);
            } else {
                newHumanControlled.add(conversationId);
            }
            setHumanControlled(newHumanControlled);

        } catch (error) {
            console.error('Error toggling human control:', error);
        }
    };

    // ==================== HANDLERS DE EVENTOS ====================
    
    const handleExport = async () => {
        try {
            setIsExporting(true);
            const imageUrl = await exportImage();
            
            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = `conversation-network-${new Date().getTime()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (err) {
            console.error('Error exportando imagen:', err);
            alert('Error al exportar la imagen');
        } finally {
            setIsExporting(false);
        }
    };

    const handleAutoRotateToggle = () => {
        setAutoRotateEnabled(!autoRotateEnabled);
        toggleAutoRotate();
    };

    const getPriorityColor = (stage: string) => {
        switch (stage) {
            case 'closed': return 'success';
            case 'negotiation': return 'warning';
            case 'interest': return 'info';
            case 'prospecting': return 'secondary';
            default: return 'light';
        }
    };

    // Obtener agentes únicos para filtros
    const uniqueAgents = [...new Set(activeConversations.map(c => c.profileUsed))];

    // ==================== EFFECTS ====================
    
    // CAMBIO: Effect mejorado para actualizar datos del Three.js
    useEffect(() => {
        console.log('🔄 Data update effect triggered:', {
            threeLoading,
            hasData: !!networkData.data,
            pointsLength: networkData.data?.points?.length || 0,
            isProcessing: networkData.isProcessing
        });

        if (threeLoading) {
            console.log('⏳ Three.js still loading, waiting...');
            return;
        }

        if (networkData.isProcessing) {
            console.log('⏳ Data still processing, waiting...');
            return;
        }

        if (!networkData.data) {
            console.log('❌ No network data available');
            return;
        }

        if (networkData.data.points.length > 0) {
            console.log('✅ Updating 3D scene with', networkData.data.points.length, 'points');
            updateData(networkData.data);
        } else {
            console.log('🗑️ No points to display, clearing scene');
            clearScene();
        }
    }, [
    networkData.data?.points?.length,
    networkData.isProcessing,
    threeLoading,
    updateData,
    clearScene
]);

    // Verificaciones
    const isEmpty = false; // Por ahora mantenemos siempre visible para debug
    const combinedError = threeError || dataError;

    // ==================== RENDER DEL COMPONENTE PRINCIPAL ====================
    
    return (
        <Card className="h-100 border-0 shadow-sm">
            {/* ==================== HEADER (SIN CAMBIOS MAYORES) ==================== */}
            <Card.Header className="bg-white border-bottom">
                <Row className="align-items-center">
                    <Col>
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faNetworkWired} className="text-primary me-2" />
                            <div>
                                <h5 className="mb-0">
                                    {language === 'es' ? 'Red de Conversaciones' : 'Conversation Network'}
                                </h5>
                                <small className="text-muted">
                                    {language === 'es' ? 'Visualización en tiempo real' : 'Real-time visualization'}
                                </small>
                            </div>
                        </div>
                    </Col>
                    <Col xs="auto">
                        <div className="d-flex gap-2 align-items-center">
                            {/* Estadísticas rápidas (sin cambios) */}
                            <div className="d-flex flex-wrap gap-2 text-center">
                                <Badge bg="info" className="p-2">
                                    <FontAwesomeIcon icon={faComments} className="me-1" />
                                    <strong>{activeConversations.length}</strong>
                                    <small className="d-block">
                                        {language === 'es' ? 'Total' : 'Total'}
                                    </small>
                                </Badge>
                                <Badge bg="success" className="p-2">
                                    <FontAwesomeIcon icon={faFire} className="me-1" />
                                    <strong>{activeConversations.filter(c => c.isActive).length}</strong>
                                    <small className="d-block">
                                        {language === 'es' ? 'Activas' : 'Active'}
                                    </small>
                                </Badge>
                                <Badge bg="warning" className="p-2">
                                    <FontAwesomeIcon icon={faHandshake} className="me-1" />
                                    <strong>{activeConversations.filter(c => c.stage === 'negotiation').length}</strong>
                                    <small className="d-block">
                                        {language === 'es' ? 'Negociando' : 'Negotiating'}
                                    </small>
                                </Badge>
                                <Badge bg="primary" className="p-2">
                                    <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                                    <strong>{activeConversations.reduce((sum, c) => sum + (c.stage === 'closed' ? c.value : 0), 0).toLocaleString()}</strong>
                                    <small className="d-block">
                                        {language === 'es' ? 'Vendido' : 'Sold'}
                                    </small>
                                </Badge>
                            </div>
                            
                            {/* NUEVO: Badge de debug */}
                            <Badge 
                                bg="dark" 
                                className="p-2 cursor-pointer" 
                                onClick={() => setShowDebugInfo(!showDebugInfo)}
                                style={{ cursor: 'pointer' }}
                            >
                                <FontAwesomeIcon icon={faCube} className="me-1" />
                                <strong>{debugInfo.fps}</strong>
                                <small className="d-block">FPS</small>
                            </Badge>
                            
                            {/* Indicador de fuente de datos (sin cambios) */}
                            <div className="mt-2 text-center">
                                <small className="text-muted">
                                    {sellerBotConversations.length > 0 ? (
                                        <>
                                            <FontAwesomeIcon icon={faRobot} className="me-1 text-success" />
                                            {language === 'es' ? 'Datos reales del SellerBot' : 'Real SellerBot data'}
                                            {lastFetch && (
                                                <span className="ms-2">
                                                    ({language === 'es' ? 'Actualizado' : 'Updated'}: {lastFetch.toLocaleTimeString()})
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faCube} className="me-1 text-warning" />
                                            {language === 'es' ? 'Datos de demostración' : 'Demo data'}
                                        </>
                                    )}
                                </small>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Card.Header>
            {/* ==================== TOOLBAR MEJORADO (CONTINUACIÓN) ==================== */}
            <div className="border-bottom bg-light p-2">
                <Row className="align-items-center g-2">
                    <Col>
                        {/* Selector de vista */}
                        <ButtonGroup size="sm">
                            {/*<Button
                                variant={viewMode === '3d-network' ? 'primary' : 'outline-primary'}
                                onClick={() => setViewMode('3d-network')}
                            >
                                <FontAwesomeIcon icon={faCube} className="me-1" />
                                3D
                            </Button>*/}
                            <Button
                                variant={viewMode === '2d-grid' ? 'primary' : 'outline-primary'}
                                onClick={() => setViewMode('2d-grid')}
                            >
                                <FontAwesomeIcon icon={faTableCells} className="me-1" />
                                Grid
                            </Button>
                            <Button
                                variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                                onClick={() => setViewMode('list')}
                            >
                                <FontAwesomeIcon icon={faList} className="me-1" />
                                Lista
                            </Button>
                            <Button
                                variant={viewMode === 'timeline' ? 'primary' : 'outline-primary'}
                                onClick={() => setViewMode('timeline')}
                            >
                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                Timeline
                            </Button>
                        </ButtonGroup>
                    </Col>
                    <Col xs="auto">
                        {/* Controles mejorados */}
                        <div className="d-flex gap-2 align-items-center">
                            {/* Filtros */}
                            <Dropdown>
                                <Dropdown.Toggle size="sm" variant="outline-secondary">
                                    <FontAwesomeIcon icon={faFilter} className="me-1" />
                                    {language === 'es' ? 'Etapa' : 'Stage'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item 
                                        active={stageFilter === 'all'}
                                        onClick={() => setStageFilter('all')}
                                    >
                                        {language === 'es' ? 'Todas' : 'All'}
                                    </Dropdown.Item>
                                    <Dropdown.Item 
                                        active={stageFilter === 'prospecting'}
                                        onClick={() => setStageFilter('prospecting')}
                                    >
                                        Prospecting
                                    </Dropdown.Item>
                                    <Dropdown.Item 
                                        active={stageFilter === 'interest'}
                                        onClick={() => setStageFilter('interest')}
                                    >
                                        Interest
                                    </Dropdown.Item>
                                    <Dropdown.Item 
                                        active={stageFilter === 'negotiation'}
                                        onClick={() => setStageFilter('negotiation')}
                                    >
                                        Negotiation
                                    </Dropdown.Item>
                                    <Dropdown.Item 
                                        active={stageFilter === 'closed'}
                                        onClick={() => setStageFilter('closed')}
                                    >
                                        Closed
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>

                            <Dropdown>
                                <Dropdown.Toggle size="sm" variant="outline-secondary">
                                    <FontAwesomeIcon icon={faUserTie} className="me-1" />
                                    {language === 'es' ? 'Agente' : 'Agent'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item 
                                        active={agentFilter === 'all'}
                                        onClick={() => setAgentFilter('all')}
                                    >
                                        {language === 'es' ? 'Todos' : 'All'}
                                    </Dropdown.Item>
                                    {uniqueAgents.map(agent => (
                                        <Dropdown.Item 
                                            key={agent}
                                            active={agentFilter === agent}
                                            onClick={() => setAgentFilter(agent)}
                                        >
                                            {agent}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>

                            {/* CAMBIO: Controles 3D mejorados */}
                            <ButtonGroup size="sm">
                                <Button
                                    variant="outline-primary"
                                    onClick={handleRefresh}
                                    disabled={isLoadingData}
                                    title="Actualizar datos"
                                >
                                    <FontAwesomeIcon 
                                        icon={faSync} 
                                        spin={isLoadingData}
                                        className="me-1" 
                                    />
                                    {language === 'es' ? 'Actualizar' : 'Refresh'}
                                </Button>
                                
                                {viewMode === '3d-network' && (
                                    <>
                                        <Button
                                            variant={autoRotateEnabled ? 'primary' : 'outline-primary'}
                                            onClick={handleAutoRotateToggle}
                                            title="Auto-rotación"
                                        >
                                            <FontAwesomeIcon icon={faSync} className="me-1" />
                                            Auto
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={resetCamera}
                                            title="Resetear cámara"
                                        >
                                            <FontAwesomeIcon icon={faHome} />
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleExport}
                                            disabled={isExporting}
                                            title="Exportar imagen"
                                        >
                                            <FontAwesomeIcon 
                                                icon={faDownload} 
                                                spin={isExporting}
                                            />
                                        </Button>
                                        {/* NUEVO: Toggle debug info */}
                                        <Button
                                            variant={showDebugInfo ? 'info' : 'outline-info'}
                                            onClick={() => setShowDebugInfo(!showDebugInfo)}
                                            title="Información de debug"
                                        >
                                            <FontAwesomeIcon icon={faCube} />
                                        </Button>
                                    </>
                                )}
                                
                                <Button
                                    variant={isFullscreen ? 'primary' : 'outline-secondary'}
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    title="Pantalla completa"
                                >
                                    <FontAwesomeIcon 
                                        icon={isFullscreen ? faCompress : faExpand} 
                                    />
                                </Button>
                            </ButtonGroup>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* ==================== CONTENIDO PRINCIPAL ==================== */}
            <Card.Body className="p-0 position-relative" style={{ paddingBottom: '60px !important' }}>
                <div className={`conversation-network-container ${isFullscreen ? 'fullscreen' : ''}`}>
                    {/* Loading state */}
                    {isLoadingData && (
                        <div className="text-center p-4">
                            <Spinner animation="border" variant="primary" className="me-2" />
                            <span>{language === 'es' ? 'Cargando conversaciones del SellerBot...' : 'Loading SellerBot conversations...'}</span>
                        </div>
                    )}

                    {/* Error state */}
                    {combinedError && !isLoadingData && (
                        <Alert variant="danger" className="m-3">
                            <strong>{language === 'es' ? 'Error:' : 'Error:'}</strong> {combinedError}
                        </Alert>
                    )}

                    {/* CAMBIO PRINCIPAL: Vista 3D mejorada */}
                    {viewMode === '3d-network' && !isLoadingData && !combinedError && (
                        <div className="position-relative h-100">
                            {/* Contenedor del Three.js mejorado */}
                            <div
                                ref={containerRef}
                                className="w-100 h-100"
                                style={{ 
                                    minHeight: '600px', // CAMBIO: Altura mínima mayor
                                    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)', // CAMBIO: Gradiente de fondo
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}
                            />
                            
                            {/* NUEVO: Panel de debug 3D */}
                            {showDebugInfo && (
                                <div className="position-absolute top-0 start-0 m-3">
                                    <div 
                                        className="bg-dark text-white rounded p-3 shadow-lg"
                                        style={{ 
                                            backgroundColor: 'rgba(0,0,0,0.9)', 
                                            fontFamily: 'monospace',
                                            fontSize: '12px',
                                            minWidth: '200px'
                                        }}
                                    >
                                        <div className="fw-bold mb-2 text-info">
                                            <FontAwesomeIcon icon={faCube} className="me-2" />
                                            Debug 3D
                                        </div>
                                        <div className="row g-1 small">
                                            <div className="col-6">FPS:</div>
                                            <div className="col-6 text-success">{debugInfo.fps}</div>
                                            
                                            <div className="col-6">Nodos:</div>
                                            <div className="col-6 text-warning">{debugInfo.visibleNodes}/{debugInfo.totalNodes}</div>
                                            
                                            <div className="col-6">Cámara X:</div>
                                            <div className="col-6">{debugInfo.cameraPosition.x}</div>
                                            
                                            <div className="col-6">Cámara Y:</div>
                                            <div className="col-6">{debugInfo.cameraPosition.y}</div>
                                            
                                            <div className="col-6">Cámara Z:</div>
                                            <div className="col-6">{debugInfo.cameraPosition.z}</div>
                                            
                                            <div className="col-6">Render:</div>
                                            <div className="col-6 text-info">{debugInfo.renderTime.toFixed(1)}ms</div>
                                        </div>
                                        
                                        <div className="mt-2 pt-2 border-top border-secondary">
                                            <div className="text-muted small">
                                                <div>🎯 Total conversaciones: {activeConversations.length}</div>
                                                <div>🤖 Agentes activos: {uniqueAgents.length}</div>
                                                <div>📊 Datos procesados: {networkData.data?.points?.length || 0}</div>
                                                <div>🔗 Conexiones: {networkData.data?.connections?.length || 0}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* NUEVO: Panel de información del network */}
                            <div className="position-absolute top-0 end-0 m-3">
                                <div className="bg-white rounded shadow-sm p-2" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
                                    <div className="small text-muted text-center">
                                        <div className="fw-bold text-primary">Red ANUBIS</div>
                                        <div><strong>{networkData.data?.points?.length || 0}</strong> nodos totales</div>
                                        <div><strong>{networkData.data?.connections?.length || 0}</strong> conexiones</div>
                                        <div><strong>{activeConversations.filter(c => c.isActive).length}</strong> activas</div>
                                        
                                        {/* NUEVO: Distribución por tipo */}
                                        <div className="mt-2 pt-2 border-top">
                                            <div className="d-flex justify-content-between">
                                                <span>🏢 Hub:</span>
                                                <span className="text-primary">1</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span>🤖 Agentes:</span>
                                                <span className="text-info">{networkData.data?.points?.filter(p => p.type === 'agent').length || 0}</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span>👤 Clientes:</span>
                                                <span className="text-success">{networkData.data?.points?.filter(p => p.type === 'client').length || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* NUEVO: Panel de controles de cámara */}
                            <div className="position-absolute bottom-0 start-0 m-3">
                                <div className="bg-dark text-white rounded p-2 shadow-lg">
                                    <div className="small">
                                        <div className="fw-bold mb-1 text-warning">Controles 3D</div>
                                        <div>🖱️ Arrastrar: Rotar cámara</div>
                                        <div>⚡ Scroll: Zoom in/out</div>
                                        <div>🎯 Click: Seleccionar nodo</div>
                                        <div>🏠 Home: Resetear vista</div>
                                    </div>
                                </div>
                            </div>

                            {/* NUEVO: Overlay de carga para procesamiento de datos */}
                            {networkData.isProcessing && (
                                <div 
                                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                    style={{ 
                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                        zIndex: 10
                                    }}
                                >
                                    <div className="text-center text-white">
                                        <Spinner animation="border" className="mb-3" />
                                        <div className="h5">Procesando red de conversaciones...</div>
                                        <div className="text-muted">
                                            Creando nodos y conexiones para {activeConversations.length} conversaciones
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* NUEVO: Overlay de error específico para Three.js */}
                            {threeError && (
                                <div 
                                    className="position-absolute top-50 start-50 translate-middle"
                                    style={{ zIndex: 15 }}
                                >
                                    <Alert variant="danger" className="text-center">
                                        <FontAwesomeIcon icon={faCube} size="2x" className="mb-2" />
                                        <div className="fw-bold">Error de visualización 3D</div>
                                        <div className="small">{threeError}</div>
                                        <Button 
                                            variant="outline-danger" 
                                            size="sm" 
                                            className="mt-2"
                                            onClick={() => window.location.reload()}
                                        >
                                            Recargar página
                                        </Button>
                                    </Alert>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ==================== VISTA GRID 2D (SIN CAMBIOS MAYORES) ==================== */}
                    {viewMode === '2d-grid' && !isLoadingData && !isEmpty && !combinedError && (
                        <div className="p-3">
                            <Row className="g-3">
                                {activeConversations.filter(conv => {
                                    if (stageFilter !== 'all' && conv.stage !== stageFilter) return false;
                                    if (agentFilter !== 'all' && conv.profileUsed !== agentFilter) return false;
                                    return true;
                                }).map(conversation => (
                                    <Col key={conversation.id} xs={12} sm={6} md={4} lg={3}>
                                        <Card 
                                            className="h-100 conversation-card border-0 shadow-sm"
                                            style={{ 
                                                cursor: 'pointer',
                                                borderLeft: `4px solid ${
                                                    conversation.stage === 'prospecting' ? '#3498db' :
                                                    conversation.stage === 'interest' ? '#f39c12' :
                                                    conversation.stage === 'negotiation' ? '#e67e22' :
                                                    conversation.stage === 'closed' ? '#27ae60' : '#bdc3c7'
                                                }`
                                            }}
                                            onClick={() => {
                                                setSelectedConversation(conversation);
                                                setShowConversationModal(true);
                                            }}
                                        >
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <h6 className="mb-0 text-truncate">
                                                        {conversation.contactName}
                                                    </h6>
                                                    <Badge bg={getPriorityColor(conversation.stage)}>
                                                        {conversation.stage}
                                                    </Badge>
                                                </div>
                                                
                                                <div className="small text-muted mb-2">
                                                    <FontAwesomeIcon icon={faPhone} className="me-1" />
                                                    {conversation.contactPhone}
                                                </div>
                                                
                                                <div className="small mb-2">
                                                    <strong>Agente:</strong> {conversation.profileUsed}
                                                </div>
                                                
                                                <div className="small mb-2">
                                                    <strong>Valor:</strong> ${conversation.value.toLocaleString()}
                                                </div>
                                                
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <small className="text-muted">
                                                        {conversation.probability}% probabilidad
                                                    </small>
                                                    <div className="d-flex gap-1">
                                                        <Button
                                                            size="sm"
                                                            variant={pausedConversations.has(conversation.id) ? 'success' : 'warning'}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleConversationPause(conversation.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon 
                                                                icon={pausedConversations.has(conversation.id) ? faPlay : faPause} 
                                                            />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant={humanControlled.has(conversation.id) ? 'primary' : 'outline-secondary'}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleHumanControl(conversation.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faUser} />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}

                    {/* ==================== VISTA LISTA (SIN CAMBIOS) ==================== */}
                    {viewMode === 'list' && !isLoadingData && !isEmpty && !combinedError && (
                        <div className="p-3">
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{language === 'es' ? 'Contacto' : 'Contact'}</th>
                                            <th>{language === 'es' ? 'Etapa' : 'Stage'}</th>
                                            <th>{language === 'es' ? 'Valor' : 'Value'}</th>
                                            <th>{language === 'es' ? 'Agente' : 'Agent'}</th>
                                            <th>{language === 'es' ? 'Acciones' : 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeConversations.filter(conv => {
                                            if (stageFilter !== 'all' && conv.stage !== stageFilter) return false;
                                            if (agentFilter !== 'all' && conv.profileUsed !== agentFilter) return false;
                                            return true;
                                        }).map(conversation => (
                                            <tr key={conversation.id} style={{ cursor: 'pointer' }}>
                                                <td onClick={() => {
                                                    setSelectedConversation(conversation);
                                                    setShowConversationModal(true);
                                                }}>
                                                    <div>
                                                        <div className="fw-semibold">{conversation.contactName}</div>
                                                        <small className="text-muted">{conversation.contactPhone}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge bg={getPriorityColor(conversation.stage)}>
                                                        {conversation.stage}
                                                    </Badge>
                                                </td>
                                                <td>${conversation.value.toLocaleString()}</td>
                                                <td>{conversation.profileUsed}</td>
                                                <td>
                                                    <ButtonGroup size="sm">
                                                        <Button
                                                            variant={pausedConversations.has(conversation.id) ? 'success' : 'warning'}
                                                            onClick={() => toggleConversationPause(conversation.id)}
                                                        >
                                                            <FontAwesomeIcon 
                                                                icon={pausedConversations.has(conversation.id) ? faPlay : faPause} 
                                                            />
                                                        </Button>
                                                        <Button
                                                            variant={humanControlled.has(conversation.id) ? 'primary' : 'outline-secondary'}
                                                            onClick={() => toggleHumanControl(conversation.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faUser} />
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            onClick={() => {
                                                                setSelectedConversation(conversation);
                                                                setShowConversationModal(true);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </Button>
                                                    </ButtonGroup>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ==================== VISTA TIMELINE (SIN CAMBIOS) ==================== */}
                    {viewMode === 'timeline' && !isLoadingData && !isEmpty && !combinedError && (
                        <div className="p-3">
                            <div className="timeline-container">
                                {activeConversations
                                    .filter(conv => {
                                        if (stageFilter !== 'all' && conv.stage !== stageFilter) return false;
                                        if (agentFilter !== 'all' && conv.profileUsed !== agentFilter) return false;
                                        return true;
                                    })
                                    .sort((a, b) => new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime())
                                    .map(conversation => (
                                        <div 
                                            key={conversation.id} 
                                            className="timeline-item mb-3 p-3 border rounded"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                setSelectedConversation(conversation);
                                                setShowConversationModal(true);
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <h6 className="mb-0 me-2">{conversation.contactName}</h6>
                                                        <Badge bg={getPriorityColor(conversation.stage)}>
                                                            {conversation.stage}
                                                        </Badge>
                                                        {conversation.isActive && (
                                                            <Badge bg="success" className="ms-2">
                                                                <FontAwesomeIcon icon={faFire} className="me-1" />
                                                                Activa
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="small text-muted mb-2">
                                                        <FontAwesomeIcon icon={faUserTie} className="me-1" />
                                                        Agente: {conversation.profileUsed} | 
                                                        <FontAwesomeIcon icon={faDollarSign} className="ms-2 me-1" />
                                                        ${conversation.value.toLocaleString()} |
                                                        <FontAwesomeIcon icon={faBullseye} className="ms-2 me-1" />
                                                        {conversation.probability}%
                                                    </div>
                                                    
                                                    <div className="small">
                                                        <strong>Último mensaje:</strong> {conversation.lastMessage}
                                                    </div>
                                                </div>
                                                
                                                <div className="text-end">
                                                    <small className="text-muted d-block">
                                                        {new Date(conversation.lastMessageTime).toLocaleString()}
                                                    </small>
                                                    <div className="mt-2">
                                                        <ButtonGroup size="sm">
                                                            <Button
                                                                variant={pausedConversations.has(conversation.id) ? 'success' : 'warning'}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleConversationPause(conversation.id);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon 
                                                                    icon={pausedConversations.has(conversation.id) ? faPlay : faPause} 
                                                                />
                                                            </Button>
                                                            <Button
                                                                variant={humanControlled.has(conversation.id) ? 'primary' : 'outline-secondary'}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleHumanControl(conversation.id);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faUser} />
                                                            </Button>
                                                        </ButtonGroup>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </Card.Body>
            {/* ==================== TOOLTIP MEJORADO DEL NETWORK 3D ==================== */}
{tooltip?.visible && tooltip?.point && (
    <div 
        className="position-absolute bg-dark text-white p-2 rounded shadow-lg"
        style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            zIndex: 1000,
            pointerEvents: 'none',
            fontSize: '0.875rem'
        }}
    >
        <div>
            <strong>{tooltip.point.label}</strong>
            {tooltip.point.type === 'hub' && tooltip.point.metadata.hubData && (
                <>
                    <div className="mt-1 text-warning">🏢 Centro ANUBIS</div>
                    <div>Conversaciones: {tooltip.point.metadata.hubData.totalConversations}</div>
                    <div>Valor total: ${tooltip.point.metadata.hubData.totalValue.toLocaleString()}</div>
                    <div>Agentes activos: {tooltip.point.metadata.hubData.activeAgents}</div>
                </>
            )}
            {tooltip.point.type === 'agent' && tooltip.point.metadata.agentData && (
                <>
                    <div className="mt-1 text-info">🤖 Agente IA</div>
                    <div>Personalidad: {tooltip.point.metadata.agentData.personality}</div>
                    <div>Conversaciones: {tooltip.point.metadata.agentData.activeConversations}</div>
                    <div>Valor total: ${tooltip.point.metadata.agentData.totalValue.toLocaleString()}</div>
                    <div>Promedio probabilidad: {tooltip.point.metadata.agentData.averageProbability.toFixed(1)}%</div>
                </>
            )}
            {tooltip.point.type === 'client' && tooltip.point.metadata.conversation && (
                <>
                    <div className="mt-1 text-success">👤 Cliente</div>
                    <div>Teléfono: {tooltip.point.metadata.conversation.contactPhone}</div>
                    <div>Etapa: {tooltip.point.metadata.conversation.stage}</div>
                    <div>Valor: ${tooltip.point.metadata.conversation.value.toLocaleString()}</div>
                    <div>Probabilidad: {tooltip.point.metadata.conversation.probability}%</div>
                    <div>Producto: {tooltip.point.metadata.conversation.productOffered}</div>
                    <div>Último mensaje: {new Date(tooltip.point.metadata.conversation.lastMessageTime).toLocaleString()}</div>
                    {tooltip.point.metadata.conversation.isActive && (
                        <div className="mt-1 text-warning">🔥 Conversación activa</div>
                    )}
                    {tooltip.point.metadata.isPaused && (
                        <div className="mt-2 pt-2 border-top">
                            <small className="text-warning">⏸️ Bot pausado</small>
                        </div>
                    )}
                    {tooltip.point.metadata.isHumanControlled && (
                        <div className="mt-2 pt-2 border-top">
                            <small className="text-info">👤 Control humano activo</small>
                        </div>
                    )}
                    <div className="mt-2 pt-2 border-top">
                        <small className="text-muted">💡 Click para ver conversación completa</small>
                    </div>
                </>
            )}
        </div>
    </div>
)}

            {/* ==================== MODAL DE CONVERSACIÓN DRAG&DROP&RESIZE (SIN CAMBIOS) ==================== */}
            {showConversationModal && selectedConversation && (
                <div className="conversation-modal-overlay position-fixed w-100 h-100" style={{ top: 0, left: 0, zIndex: 1055, backgroundColor: 'rgba(0,0,0,0.3)' }}>
                    <div
                        className="conversation-modal position-absolute bg-white rounded shadow-lg d-flex flex-column"
                        style={{
                            left: `${modalPosition.x}px`,
                            top: `${modalPosition.y}px`,
                            width: `${modalSize.width}px`,
                            height: `${modalSize.height}px`,
                            minWidth: '500px',
                            minHeight: '400px',
                            maxWidth: '95vw',
                            maxHeight: '95vh',
                            cursor: isDragging ? 'grabbing' : 'default'
                        }}
                    >
                        {/* Header del modal - draggable */}
                        <div 
                            className="modal-header bg-primary text-white p-3 d-flex justify-content-between align-items-center"
                            style={{ cursor: 'grab' }}
                            onMouseDown={(e) => {
                                setIsDragging(true);
                                setDragStart({ x: e.clientX - modalPosition.x, y: e.clientY - modalPosition.y });
                            }}
                        >
                            <div>
                                <h5 className="mb-0">
                                    <FontAwesomeIcon icon={faComments} className="me-2" />
                                    {selectedConversation.contactName}
                                </h5>
                                <small className="opacity-75">
                                    {selectedConversation.contactPhone} • {selectedConversation.stage}
                                </small>
                            </div>
                            <Button
                                variant="link"
                                className="text-white p-0"
                                onClick={() => setShowConversationModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </Button>
                        </div>

                        {/* Contenido del modal */}
                        <div className="modal-body flex-grow-1 p-3 overflow-auto">
                            <Row className="g-3">
                                {/* Información del contacto */}
                                <Col md={6}>
                                    <Card>
                                        <Card.Header>
                                            <h6 className="mb-0">
                                                <FontAwesomeIcon icon={faUser} className="me-2" />
                                                {language === 'es' ? 'Información del Contacto' : 'Contact Information'}
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="mb-2">
                                                <strong>{language === 'es' ? 'Nombre:' : 'Name:'}</strong>
                                                <div>{selectedConversation.contactName}</div>
                                            </div>
                                            <div className="mb-2">
                                                <strong>{language === 'es' ? 'Teléfono:' : 'Phone:'}</strong>
                                                <div>{selectedConversation.contactPhone}</div>
                                            </div>
                                            {selectedConversation.contactEmail && (
                                                <div className="mb-2">
                                                    <strong>Email:</strong>
                                                    <div>{selectedConversation.contactEmail}</div>
                                                </div>
                                            )}
                                            <div className="mb-2">
                                                <strong>{language === 'es' ? 'Etapa:' : 'Stage:'}</strong>
                                                <div>
                                                    <Badge bg={getPriorityColor(selectedConversation.stage)}>
                                                        {selectedConversation.stage}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <div className="mb-2">
                                                <strong>{language === 'es' ? 'Valor estimado:' : 'Estimated value:'}</strong>
                                                <div>${selectedConversation.value.toLocaleString()}</div>
                                            </div>
                                            <div className="mb-2">
                                                <strong>{language === 'es' ? 'Probabilidad:' : 'Probability:'}</strong>
                                                <div>
                                                    <div className="d-flex align-items-center">
                                                        <div className="progress flex-grow-1 me-2" style={{ height: '20px' }}>
                                                            <div 
                                                                className={`progress-bar bg-${
                                                                    selectedConversation.probability >= 75 ? 'success' :
                                                                    selectedConversation.probability >= 50 ? 'warning' : 'danger'
                                                                }`}
                                                                style={{ width: `${selectedConversation.probability}%` }}
                                                            >
                                                                {selectedConversation.probability}%
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Información del bot */}
                                <Col md={6}>
                                    <Card>
                                        <Card.Header>
                                            <h6 className="mb-0">
                                                <FontAwesomeIcon icon={faRobot} className="me-2" />
                                                {language === 'es' ? 'Información del Bot' : 'Bot Information'}
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="mb-2">
                                                <strong>{language === 'es' ? 'Agente utilizado:' : 'Agent used:'}</strong>
                                                <div>{selectedConversation.profileUsed}</div>
                                            </div>
                                            <div className="mb-2">
                                                <strong>{language === 'es' ? 'Producto ofrecido:' : 'Product offered:'}</strong>
                                                <div>{selectedConversation.productOffered}</div>
                                            </div>
                                            <div className="mb-2">
                                                <strong>WhatsApp Chat ID:</strong>
                                                <div className="small font-monospace text-muted">
                                                    {selectedConversation.whatsappChatId}
                                                </div>
                                            </div>
                                            <div className="mb-2">
                                                <strong>{language === 'es' ? 'Estado:' : 'Status:'}</strong>
                                                <div>
                                                    {selectedConversation.isActive ? (
                                                        <Badge bg="success">
                                                            <FontAwesomeIcon icon={faFire} className="me-1" />
                                                            {language === 'es' ? 'Activa' : 'Active'}
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="secondary">
                                                            {language === 'es' ? 'Inactiva' : 'Inactive'}
                                                        </Badge>
                                                    )}
                                                    {pausedConversations.has(selectedConversation.id) && (
                                                        <Badge bg="warning" className="ms-2">
                                                            <FontAwesomeIcon icon={faPause} className="me-1" />
                                                            {language === 'es' ? 'Pausada' : 'Paused'}
                                                        </Badge>
                                                    )}
                                                    {humanControlled.has(selectedConversation.id) && (
                                                        <Badge bg="info" className="ms-2">
                                                            <FontAwesomeIcon icon={faUser} className="me-1" />
                                                            {language === 'es' ? 'Control Humano' : 'Human Control'}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mb-2">
                                                <strong>{language === 'es' ? 'Último mensaje:' : 'Last message:'}</strong>
                                                <div className="small text-muted">
                                                    {new Date(selectedConversation.lastMessageTime).toLocaleString()}
                                                </div>
                                            </div>
                                            {selectedConversation.source && (
                                                <div className="mb-2">
                                                    <strong>{language === 'es' ? 'Fuente:' : 'Source:'}</strong>
                                                    <div>
                                                        <Badge bg="info">
                                                            {selectedConversation.source === 'whatsapp_sellerbot' ? (
                                                                <>
                                                                    <FontAwesomeIcon icon={faRobot} className="me-1" />
                                                                    SellerBot WhatsApp
                                                                </>
                                                            ) : (
                                                                selectedConversation.source
                                                            )}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Historial de mensajes */}
                                <Col xs={12}>
                                    <Card>
                                        <Card.Header>
                                            <h6 className="mb-0">
                                                <FontAwesomeIcon icon={faComments} className="me-2" />
                                                {language === 'es' ? 'Historial de Mensajes' : 'Message History'}
                                                <Badge bg="secondary" className="ms-2">
                                                    {selectedConversation.messages?.length || 0}
                                                </Badge>
                                            </h6>
                                        </Card.Header>
                                        <Card.Body style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            {selectedConversation.messages && selectedConversation.messages.length > 0 ? (
                                                <div className="conversation-messages">
                                                    {selectedConversation.messages.map((message, index) => (
                                                        <div 
                                                            key={message.id || index} 
                                                            className={`message-bubble mb-3 ${message.sender === 'bot' ? 'message-bot' : 'message-client'}`}
                                                        >
                                                            <div className={`d-flex ${message.sender === 'bot' ? 'justify-content-start' : 'justify-content-end'}`}>
                                                                <div 
                                                                    className={`p-3 rounded max-width-75 ${
                                                                        message.sender === 'bot' 
                                                                            ? 'bg-light border' 
                                                                            : 'bg-primary text-white'
                                                                    }`}
                                                                    style={{ maxWidth: '75%' }}
                                                                >
                                                                    <div className="message-content">
                                                                        {message.text}
                                                                    </div>
                                                                    <div className={`message-meta mt-2 small ${
                                                                        message.sender === 'bot' ? 'text-muted' : 'text-white-50'
                                                                    }`}>
                                                                        <FontAwesomeIcon 
                                                                            icon={message.sender === 'bot' ? faRobot : faUser} 
                                                                            className="me-1" 
                                                                        />
                                                                        {message.sender === 'bot' ? 'SellerBot' : selectedConversation.contactName}
                                                                        <span className="ms-2">
                                                                            {new Date(message.timestamp).toLocaleTimeString()}
                                                                        </span>
                                                                        {message.status && (
                                                                            <span className="ms-2">
                                                                                {message.status === 'read' && '✓✓'}
                                                                                {message.status === 'delivered' && '✓'}
                                                                                {message.status === 'sent' && '⏳'}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center text-muted p-4">
                                                    <FontAwesomeIcon icon={faComments} size="2x" className="mb-2" />
                                                    <div>
                                                        {language === 'es' 
                                                            ? 'No hay mensajes disponibles para mostrar' 
                                                            : 'No messages available to display'}
                                                    </div>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </div>

                        {/* Footer del modal con acciones */}
                        <div className="modal-footer bg-light p-3 d-flex justify-content-between">
                            <div className="d-flex gap-2">
                                {/* Controles de conversación */}
                                <Button
                                    size="sm"
                                    variant={pausedConversations.has(selectedConversation.id) ? 'success' : 'warning'}
                                    onClick={() => toggleConversationPause(selectedConversation.id)}
                                >
                                    <FontAwesomeIcon 
                                        icon={pausedConversations.has(selectedConversation.id) ? faPlay : faPause} 
                                        className="me-1" 
                                    />
                                    {pausedConversations.has(selectedConversation.id) 
                                        ? (language === 'es' ? 'Reanudar' : 'Resume')
                                        : (language === 'es' ? 'Pausar' : 'Pause')
                                    }
                                </Button>

                                <Button
                                    size="sm"
                                    variant={humanControlled.has(selectedConversation.id) ? 'primary' : 'outline-secondary'}
                                    onClick={() => toggleHumanControl(selectedConversation.id)}
                                >
                                    <FontAwesomeIcon icon={faUser} className="me-1" />
                                    {humanControlled.has(selectedConversation.id) 
                                        ? (language === 'es' ? 'Control Activo' : 'Active Control')
                                        : (language === 'es' ? 'Tomar Control' : 'Take Control')
                                    }
                                </Button>

                                {/* Botón para llamar (simulado) */}
                                <Button
                                    size="sm"
                                    variant="outline-success"
                                    onClick={() => {
                                        window.open(`tel:${selectedConversation.contactPhone.replace(/\D/g, '')}`, '_self');
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPhone} className="me-1" />
                                    {language === 'es' ? 'Llamar' : 'Call'}
                                </Button>
                            </div>

                            <div className="d-flex gap-2">
                                {/* Botón para enviar mensaje */}
                                <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={async () => {
                                        const message = prompt(language === 'es' ? 'Escribe tu mensaje:' : 'Write your message:');
                                        if (message && message.trim()) {
                                            try {
                                                await handleSendMessage(selectedConversation.id, message.trim());
                                            } catch (error) {
                                                console.error('Error sending message:', error);
                                            }
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                                    {language === 'es' ? 'Enviar' : 'Send'}
                                </Button>

                                {/* Botón cerrar */}
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={() => setShowConversationModal(false)}
                                >
                                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                                    {language === 'es' ? 'Cerrar' : 'Close'}
                                </Button>
                            </div>
                        </div>

                        {/* Handle de resize en la esquina inferior derecha */}
                        <div
                            className="position-absolute"
                            style={{
                                bottom: 0,
                                right: 0,
                                width: '20px',
                                height: '20px',
                                cursor: 'se-resize',
                                background: 'linear-gradient(-45deg, transparent 0%, transparent 40%, #ccc 40%, #ccc 60%, transparent 60%)',
                            }}
                            onMouseDown={(e) => {
                                setIsResizing(true);
                                setDragStart({ x: e.clientX, y: e.clientY });
                            }}
                        />
                    </div>
                </div>
            )}

            {/* ==================== FOOTER INFORMATIVO MEJORADO ==================== */}
            <Card.Footer className="bg-light">
                <Row className="align-items-center">
                    <Col>
                        <small className="text-muted">
                            {sellerBotConversations.length > 0 ? (
                                <>
                                    <FontAwesomeIcon icon={faRobot} className="me-1 text-success" />
                                    {language === 'es' 
                                        ? `Conectado al SellerBot - ${activeConversations.length} conversaciones en tiempo real`
                                        : `Connected to SellerBot - ${activeConversations.length} real-time conversations`
                                    }
                                    {/* NUEVO: Información adicional de debug */}
                                    {showDebugInfo && (
                                        <span className="ms-3 text-info">
                                            | 3D: {debugInfo.fps} FPS | Nodos: {debugInfo.visibleNodes} | Render: {debugInfo.renderTime.toFixed(1)}ms
                                        </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faCube} className="me-1 text-warning" />
                                    {language === 'es' 
                                        ? 'Esperando conexión con SellerBot - Mostrando datos de demostración'
                                        : 'Waiting for SellerBot connection - Showing demo data'
                                    }
                                </>
                            )}
                        </small>
                    </Col>
                    {sellerBotConversations.length > 0 && lastFetch && (
                        <Col xs="auto">
                            <small className="text-muted">
                                {language === 'es' ? 'Última actualización' : 'Last update'}: {lastFetch.toLocaleTimeString()}
                            </small>
                        </Col>
                    )}
                </Row>
            </Card.Footer>
        </Card>
    );

    // ==================== EFECTOS DE DRAG & DROP (SIN CAMBIOS) ==================== 
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                setModalPosition({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y
                });
            }
            if (isResizing) {
                const newWidth = e.clientX - modalPosition.x;
                const newHeight = e.clientY - modalPosition.y;
                setModalSize({
                    width: Math.max(500, newWidth),
                    height: Math.max(400, newHeight)
                });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setIsResizing(false);
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, dragStart, modalPosition]);
};

export default ConversationNetwork;

// ==================== RESUMEN DE CAMBIOS REALIZADOS ====================

/**
 * CAMBIOS PRINCIPALES IMPLEMENTADOS:
 * 
 * 1. ✅ HOOK THREE.JS COMPLETAMENTE REESCRITO:
 *    - Loop de animación continuo funcionando
 *    - Posición de cámara optimizada (80, 60, 80)
 *    - Iluminación mejorada con múltiples fuentes de luz
 *    - Grid y ejes de coordenadas visibles
 *    - Controles orbitales mejorados con límites
 *    - Raycasting para detección de clicks
 * 
 * 2. ✅ SISTEMA DE NODOS MEJORADO:
 *    - Hub ANUBIS central más prominente (tamaño 15)
 *    - Agentes distribuidos en círculo (radio 35)
 *    - Clientes distribuidos alrededor de agentes
 *    - Animaciones de entrada suaves
 *    - Colores específicos por tipo y estado
 * 
 * 3. ✅ HERRAMIENTAS DE DEBUG 3D:
 *    - Panel de información FPS en tiempo real
 *    - Debug info togglable con botón
 *    - Panel de controles de cámara visible
 *    - Panel de información del network
 *    - Overlay de estados de carga/error
 * 
 * 4. ✅ OPTIMIZACIONES DE RENDIMIENTO:
 *    - Gestión de memoria mejorada
 *    - Disposición correcta de geometrías y materiales
 *    - Loop de animación optimizado
 *    - Límites de zoom y rotación
 * 
 * 5. ✅ MEJORAS VISUALES:
 *    - Fondo degradado oscuro para mejor contraste
 *    - Conexiones animadas con partículas de flujo
 *    - Sombras y iluminación realista
 *    - Tooltip mejorado con información detallada
 * 
 * UBICACIÓN DE LOS ARCHIVOS:
 * - Reemplazar completamente el archivo ConversationNetwork.tsx original
 * - Los cambios son compatibles con la estructura existente
 * - No se requieren archivos adicionales
 * 
 * PRÓXIMOS PASOS PARA IMPLEMENTAR:
 * 1. Reemplazar el contenido del archivo ConversationNetwork.tsx
 * 2. Verificar que las importaciones estén correctas
 * 3. Probar la renderización 3D
 * 4. Ajustar posiciones de nodos si es necesario
 * 5. Optimizar rendimiento según la cantidad de conversaciones
 */